import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {InputAdornment} from "@mui/material";

import {ReactComponent as EditIcon} from "assets/img/edit-2-linear-dark.svg";
import {ReactComponent as EditDisabledIcon} from "assets/img/edit-2-linear.svg";
import {blue100, blue500} from "assets/jss/main";

import InputTextField from "./InputTextField";

/**
 * Compact (40px) text field with a pencil on the right (DS "Form / Text Field").
 * The value is handed over on blur or Enter, once trimmed, changed and accepted by
 * `validate`; a refused value stays in the field, in error, until corrected.
 * `mark` shows before the pencil while the text is exactly `markedValue`.
 */
EditableTextField.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['text', 'date']),
    inputMode: PropTypes.string,
    validate: PropTypes.func,
    highlighted: PropTypes.bool,
    disabled: PropTypes.bool,
    markedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mark: PropTypes.node,
};

const toText = (value) => (null === value || undefined === value ? '' : String(value));

function EditableTextField({value, placeholder, onChange, type = 'text', inputMode, validate = () => true, highlighted = false, disabled = false, markedValue = null, mark = null}) {
    const [draft, setDraft] = useState(toText(value));
    const [invalid, setInvalid] = useState(false);

    useEffect(() => {
        setDraft(toText(value));
        setInvalid(false);
    }, [value]);

    const marked = null !== mark && null !== markedValue && '' !== toText(markedValue) && draft.trim() === toText(markedValue);

    const commit = () => {
        const text = draft.trim();
        if ('' === text || text === toText(value)) {
            setDraft(toText(value));
            setInvalid(false);

            return;
        }
        if (!validate(text)) {
            setInvalid(true);

            return;
        }
        setInvalid(false);
        onChange(text);
    };

    return (
        <InputTextField
            hasAdornment
            fullWidth
            type={type}
            value={draft}
            placeholder={placeholder}
            disabled={disabled}
            error={invalid}
            inputProps={{inputMode}}
            onChange={(event) => {
                setDraft(event.target.value);
                setInvalid(false);
            }}
            onBlur={commit}
            onKeyDown={(event) => 'Enter' === event.key && event.target.blur()}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end" sx={{gap: '8px', '& svg': {width: '16px', height: '16px'}}}>
                        {marked && mark}
                        {disabled ? <EditDisabledIcon/> : <EditIcon/>}
                    </InputAdornment>
                ),
            }}
            sx={{
                '& .MuiInputBase-root': {height: '40px', paddingRight: '16px', backgroundColor: disabled ? undefined : '#fff'},
                '& .MuiInputBase-root input': {padding: '0 16px'},
                '& .MuiInputBase-root:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) fieldset': highlighted ? {borderColor: blue500} : {},
                '& .MuiInputBase-root.Mui-focused:not(.Mui-error) fieldset': {boxShadow: `0px 0px 0px 1px ${blue100}`},
            }}
        />
    );
}

export default EditableTextField;
